import { proxy } from 'valtio'
import { subscribeKey } from 'valtio/utils'

type ColumnSelection = string[]

type ColumnSelectionAtom = { selection: ColumnSelection; executedSelection: ColumnSelection }

// ---------------------------------------------------------------------------
// Default values
// ---------------------------------------------------------------------------
const defaultColumnSelectionForConfiguredOrders: ColumnSelection = [
  'name',
  'eligibilityType',
  'eligibilityDate',
  'forcevehicleid',
  'priority',
  'loads',
  'company',
  'P_address',
  'P_addresscheck',
  'P_lat',
  'P_lng',
  'P_notes',
  'P_servicetime',
  'P_tagsin',
  'P_tagsout',
  'P_tws_s',
  'P_tws_e',
  'P_tws_s_1',
  'P_tws_e_1',
  'P_tws_s_2',
  'P_tws_e_2',
  'P_customfields',
  'P_phone',
  'P_email',
  'P_barcodes',
  'D_address',
  'D_addresscheck',
  'D_lat',
  'D_lng',
  'D_notes',
  'D_servicetime',
  'D_tagsin',
  'D_tagsout',
  'D_tws_s',
  'D_tws_e',
  'D_tws_s_1',
  'D_tws_e_1',
  'D_tws_s_2',
  'D_tws_e_2',
  'D_customfields',
  'D_phone',
  'D_email',
  'D_barcodes',
  'S_address',
  'S_addresscheck',
  'S_lat',
  'S_lng',
  'S_notes',
  'S_servicetime',
  'S_tagsin',
  'S_tagsout',
  'S_tws_s',
  'S_tws_e',
  'S_tws_s_1',
  'S_tws_e_1',
  'S_tws_s_2',
  'S_tws_e_2',
  'S_customfields',
  'S_phone',
  'S_email',
  'S_barcodes',
]

const defaultColumnSelectionForExecutedOrders: ColumnSelection = [
  'name',

  'P_status',
  'P_statusreason',
  'P_statustime',
  'P_address',
  'P_lat',
  'P_lng',
  'P_lastdriverupdate',
  'P_timeindate',
  'P_timeoutdate',
  'P_autotimeindate',
  'P_autotimeoutdate',
  'P_podnote',
  'P_podnotetime',
  'P_podnotelat',
  'P_podnotelng',
  'P_loads',
  'P_barcodes',
  'P_customfields',

  'D_status',
  'D_statusreason',
  'D_statustime',
  'D_address',
  'D_lat',
  'D_lng',
  'D_lastdriverupdate',
  'D_timeindate',
  'D_timeoutdate',
  'D_autotimeindate',
  'D_autotimeoutdate',
  'D_podnote',
  'D_podnotetime',
  'D_podnotelat',
  'D_podnotelng',
  'D_loads',
  'D_barcodes',
  'D_customfields',

  'S_status',
  'S_statusreason',
  'S_statustime',
  'S_address',
  'S_lat',
  'S_lng',
  'S_lastdriverupdate',
  'S_timeindate',
  'S_timeoutdate',
  'S_autotimeindate',
  'S_autotimeoutdate',
  'S_podnote',
  'S_podnotetime',
  'S_podnotelat',
  'S_podnotelng',
  'S_loads',
  'S_barcodes',
  'S_customfields',
]

// ---------------------------------------------------------------------------
// Restore from Local Storage
// ---------------------------------------------------------------------------

const localStorageId = 'routemanager/v1/atoms/exportOrdersModal/columnSelection'
const localStorageExecutedId =
  'routemanager/v1/atoms/exportOrdersModal/columnSelectionExecutedOrders'

const localStorageValue = localStorage.getItem(localStorageId)
const localStorageValueExecutedOrders = localStorage.getItem(localStorageExecutedId)

const initialSelection = (
  localStorageValue ? JSON.parse(localStorageValue) : defaultColumnSelectionForConfiguredOrders
) as ColumnSelection

const initialSelectionExecuted = (
  localStorageValueExecutedOrders
    ? JSON.parse(localStorageValueExecutedOrders)
    : defaultColumnSelectionForExecutedOrders
) as ColumnSelection

const createDefaultSelection = (): ColumnSelectionAtom => ({
  selection: initialSelection,
  executedSelection: initialSelectionExecuted,
})

export const columnSelectionAtom = proxy<ColumnSelectionAtom>(createDefaultSelection())

export function resetColumnSelection() {
  columnSelectionAtom.selection = defaultColumnSelectionForConfiguredOrders
  columnSelectionAtom.executedSelection = defaultColumnSelectionForExecutedOrders
}

export const setColumnSelection = (value: string[]) => {
  columnSelectionAtom.selection = value
}

export const setColumnSelectionExecuted = (value: string[]) => {
  columnSelectionAtom.executedSelection = value
}

subscribeKey(columnSelectionAtom, 'selection', () => {
  localStorage.setItem(localStorageId, JSON.stringify(columnSelectionAtom.selection))
})

subscribeKey(columnSelectionAtom, 'executedSelection', () => {
  localStorage.setItem(
    localStorageExecutedId,
    JSON.stringify(columnSelectionAtom.executedSelection),
  )
})
